import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, Download, Shield, FileText, Eye, EyeOff, Search, AlertTriangle } from 'lucide-react';
import { format, subDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Compliance() {
  const [scheduleRegister, setScheduleRegister] = useState([]);
  const [consentLogs, setConsentLogs] = useState([]);
  const [scheduledMedicines, setScheduledMedicines] = useState([]);
  const [hsnQuery, setHsnQuery] = useState('');
  const [hsnResult, setHsnResult] = useState(null);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [inspectorMode, setInspectorMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async function() {
    setLoading(true);
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      const [registerRes, consentRes, medsRes] = await Promise.all([
        axios.get(`${API}/compliance/schedule-register?start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/compliance/consent-logs?start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/compliance/medicines/scheduled`)
      ]);
      
      setScheduleRegister(registerRes.data);
      setConsentLogs(consentRes.data);
      setScheduledMedicines(medsRes.data);
    } catch (error) {
      toast.error('Failed to fetch compliance data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  async function lookupHSN() {
    if (!hsnQuery.trim()) return;
    try {
      const res = await axios.get(`${API}/compliance/hsn-lookup?hsn_code=${hsnQuery}`);
      setHsnResult(res.data);
    } catch (error) {
      toast.error('HSN lookup failed');
    }
  }

  async function exportScheduleRegister(scheduleType) {
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      const typeParam = scheduleType ? `&schedule_type=${scheduleType}` : '';
      
      const response = await axios.get(
        `${API}/compliance/schedule-register/export?start_date=${startStr}&end_date=${endStr}${typeParam}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = scheduleType ? `Schedule_${scheduleType}_Register.csv` : 'Schedule_Register.csv';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Register exported');
    } catch (error) {
      toast.error('Export failed');
    }
  }

  function countBySchedule(type) {
    let count = 0;
    for (let i = 0; i < scheduleRegister.length; i++) {
      if (scheduleRegister[i].schedule_type === type) count++;
    }
    return count;
  }

  function renderScheduleRows() {
    if (scheduleRegister.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center py-8 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No Schedule H1/X sales recorded for this period</p>
          </td>
        </tr>
      );
    }

    const rows = [];
    for (let i = 0; i < scheduleRegister.length; i++) {
      const entry = scheduleRegister[i];
      rows.push(
        <tr key={entry.id} className="border-b hover:bg-slate-50">
          <td className="py-2 px-3 text-sm">{i + 1}</td>
          <td className="py-2 px-3 text-sm">{format(new Date(entry.sale_date), 'dd/MM/yyyy')}</td>
          <td className="py-2 px-3 text-xs font-mono">{entry.bill_number}</td>
          <td className="py-2 px-3 text-sm">{entry.brand_name}</td>
          <td className="py-2 px-3 text-sm">
            <Badge className={entry.schedule_type === 'X' ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-800'}>
              {entry.schedule_type}
            </Badge>
          </td>
          <td className="py-2 px-3 text-sm">{entry.quantity}</td>
          <td className="py-2 px-3 text-sm">{entry.patient_name || '-'}</td>
          <td className="py-2 px-3 text-sm">
            <p className="font-medium">{entry.prescriber_name}</p>
            <p className="text-xs text-slate-500">{entry.prescriber_address}</p>
          </td>
        </tr>
      );
    }
    return rows;
  }

  function renderConsentRows() {
    if (consentLogs.length === 0) {
      return (
        <tr>
          <td colSpan="5" className="text-center py-8 text-slate-400">
            <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No consent records for this period</p>
          </td>
        </tr>
      );
    }

    const rows = [];
    for (let i = 0; i < consentLogs.length; i++) {
      const log = consentLogs[i];
      rows.push(
        <tr key={log.id} className="border-b hover:bg-slate-50">
          <td className="py-2 px-3 text-sm">{format(new Date(log.consent_timestamp), 'dd/MM/yyyy HH:mm')}</td>
          <td className="py-2 px-3 text-sm">{log.patient_name}</td>
          <td className="py-2 px-3 text-sm">{log.consent_type}</td>
          <td className="py-2 px-3 text-sm">
            <Badge className={log.consent_for_refill_reminders ? 'bg-mint-100 text-mint-800' : 'bg-slate-100 text-slate-600'}>
              {log.consent_for_refill_reminders ? 'Yes' : 'No'}
            </Badge>
          </td>
          <td className="py-2 px-3 text-xs font-mono">{log.bill_id.substring(0, 8)}...</td>
        </tr>
      );
    }
    return rows;
  }

  function renderScheduledMedicines() {
    if (scheduledMedicines.length === 0) {
      return (
        <div className="text-center py-8 text-slate-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No Schedule H1/X medicines in inventory</p>
        </div>
      );
    }

    const cards = [];
    for (let i = 0; i < scheduledMedicines.length; i++) {
      const med = scheduledMedicines[i];
      cards.push(
        <Card key={med.id} className="border-red-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{med.brand_name}</h3>
                  <Badge className={med.schedule_type === 'X' ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-800'}>
                    <Shield className="w-3 h-3 mr-1" />
                    {med.schedule_type}
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">{med.salt_composition}</p>
              </div>
              {!inspectorMode && (
                <div className="text-right">
                  <p className="font-mono font-bold">₹{med.mrp}</p>
                  <p className="text-sm text-slate-500">Stock: {med.total_stock}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }
    return cards;
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Compliance & Legal</h1>
          <p className="text-slate-500 text-sm">Schedule H1/X Register, DPDP Consent Logs, HSN Utility</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={inspectorMode ? "default" : "outline"}
            onClick={function() { setInspectorMode(!inspectorMode); }}
            className={inspectorMode ? "bg-navy-900" : ""}
            data-testid="inspector-mode-btn"
          >
            {inspectorMode ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {inspectorMode ? 'Exit Inspector View' : 'Inspector View'}
          </Button>
        </div>
      </div>

      {inspectorMode && (
        <div className="bg-navy-900 text-white p-4 rounded-lg mb-6">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-semibold">INSPECTOR MODE ACTIVE</span>
          </div>
          <p className="text-sm text-navy-300 mt-1">
            Sensitive business data (profit margins, purchase prices) is hidden. Only compliance data is visible.
          </p>
        </div>
      )}

      {/* Date Filter */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>From:</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-40">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(startDate, 'dd/MM/yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={function(date) { if (date) setStartDate(date); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <Label>To:</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-40">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(endDate, 'dd/MM/yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={function(date) { if (date) setEndDate(date); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button onClick={fetchData} disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Schedule H1 Sales</p>
                <p className="text-xl font-bold text-navy-900">{countBySchedule('H1')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Schedule X Sales</p>
                <p className="text-xl font-bold text-navy-900">{countBySchedule('X')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-mint-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-mint-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Consent Records</p>
                <p className="text-xl font-bold text-navy-900">{consentLogs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-navy-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Controlled Items</p>
                <p className="text-xl font-bold text-navy-900">{scheduledMedicines.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="register" className="space-y-4">
        <TabsList>
          <TabsTrigger value="register" data-testid="tab-register">Schedule H1/X Register</TabsTrigger>
          <TabsTrigger value="consent" data-testid="tab-consent">DPDP Consent Logs</TabsTrigger>
          <TabsTrigger value="hsn" data-testid="tab-hsn">HSN/GST Lookup</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">Controlled Inventory</TabsTrigger>
        </TabsList>

        {/* Schedule Register Tab */}
        <TabsContent value="register">
          <Card>
            <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Schedule H1/X Sales Register (Form 35)</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={function() { exportScheduleRegister('H1'); }}>
                  <Download className="w-4 h-4 mr-1" /> H1 Register
                </Button>
                <Button variant="outline" size="sm" onClick={function() { exportScheduleRegister('X'); }}>
                  <Download className="w-4 h-4 mr-1" /> X Register
                </Button>
                <Button size="sm" onClick={function() { exportScheduleRegister(null); }} className="bg-navy-900">
                  <Download className="w-4 h-4 mr-1" /> Export All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-96">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3">S.No</th>
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-left py-2 px-3">Bill No.</th>
                      <th className="text-left py-2 px-3">Medicine</th>
                      <th className="text-left py-2 px-3">Schedule</th>
                      <th className="text-left py-2 px-3">Qty</th>
                      <th className="text-left py-2 px-3">Patient</th>
                      <th className="text-left py-2 px-3">Prescriber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderScheduleRows()}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DPDP Consent Tab */}
        <TabsContent value="consent">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-lg">DPDP Act Consent Logs (Data Protection)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-96">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3">Timestamp</th>
                      <th className="text-left py-2 px-3">Patient Name</th>
                      <th className="text-left py-2 px-3">Consent Type</th>
                      <th className="text-left py-2 px-3">Refill Reminders</th>
                      <th className="text-left py-2 px-3">Bill ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderConsentRows()}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HSN Lookup Tab */}
        <TabsContent value="hsn">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-lg">HSN Code & GST Rate Lookup</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <Label>Enter HSN Code</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="e.g., 30041020"
                      value={hsnQuery}
                      onChange={function(e) { setHsnQuery(e.target.value); }}
                      data-testid="hsn-input"
                    />
                    <Button onClick={lookupHSN} data-testid="hsn-lookup-btn">
                      <Search className="w-4 h-4 mr-2" /> Lookup
                    </Button>
                  </div>
                </div>
              </div>

              {hsnResult && (
                <Card className="bg-mint-50 border-mint-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">HSN Code</p>
                        <p className="font-mono font-bold text-lg">{hsnResult.hsn_code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">GST Rate</p>
                        <p className="font-bold text-lg text-mint-600">{hsnResult.gst_rate}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Description</p>
                        <p className="font-medium">{hsnResult.description}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Source</p>
                        <Badge className={hsnResult.source === 'verified' ? 'bg-mint-100 text-mint-800' : 'bg-amber-100 text-amber-800'}>
                          {hsnResult.source}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="mt-6">
                <h3 className="font-semibold mb-3">Common HSN Codes for Medicines</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="p-3 bg-slate-50 rounded">
                    <span className="font-mono text-sm">30041010</span> - Antibiotics (Penicillins) - <span className="text-mint-600 font-bold">12%</span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <span className="font-mono text-sm">30049023</span> - Antidiabetic drugs - <span className="text-mint-600 font-bold">5%</span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <span className="font-mono text-sm">30049011</span> - Ayurvedic medicines - <span className="text-mint-600 font-bold">5%</span>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <span className="font-mono text-sm">30049031</span> - Anti-cancer drugs - <span className="text-amber-600 font-bold">18%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Controlled Inventory Tab */}
        <TabsContent value="inventory">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-lg">Schedule H1/X Medicines in Inventory</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderScheduledMedicines()}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Compliance;
